/**
 * OverviewTab - Overview statistics and configuration tab
 *
 * Displays key metrics, refresh settings, and view configuration
 */

import React from 'react';
import { FileText, Layers, HardDrive, Clock, RefreshCw, Tag } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { View } from '@/types/views';

interface OverviewTabProps {
  view: View;
}

/**
 * Format bytes to human-readable size
 */
const formatBytes = (bytes?: number | null): string => {
  // Distinguish between null/undefined (unknown) and 0 (actually zero)
  if (bytes === null || bytes === undefined) return 'N/A';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Format number with commas
 */
const formatNumber = (num?: number): string => {
  if (num === undefined || num === null) return 'N/A';
  return num.toLocaleString();
};

/**
 * Format date in 12-hour local time with timezone abbreviation
 */
const formatDate = (date: string | null): string => {
  if (!date) return 'Never';
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(new Date(date));
  } catch {
    return date;
  }
};


/**
 * Get refresh schedule label
 */
const getScheduleLabel = (schedule?: string): string => {
  if (!schedule) return 'None';
  const labels: Record<string, string> = {
    on_ingestion: 'On Ingestion',
    hourly: 'Hourly',
    daily: 'Daily',
    weekly: 'Weekly',
    manual: 'Manual Only',
  };
  return labels[schedule] || schedule;
};

/**
 * OverviewTab Component
 */
export const OverviewTab: React.FC<OverviewTabProps> = ({ view }) => {
  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">File Count</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(view.file_count)}</div>
            <p className="text-xs text-muted-foreground">Files matched by rules</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Rows</CardTitle>
            <Layers className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(view.total_rows)}</div>
            <p className="text-xs text-muted-foreground">Estimated row count</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Size</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatBytes(view.total_size_bytes)}</div>
            <p className="text-xs text-muted-foreground">Estimated data size</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Refresh</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-bold">{formatDate(view.last_refresh_at)}</div>
            <p className="text-xs text-muted-foreground">
              {view.next_refresh_at ? `Next: ${formatDate(view.next_refresh_at)}` : 'No schedule'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Refresh Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Refresh Configuration
          </CardTitle>
          <CardDescription>Automatic refresh settings for this view</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Auto-Refresh</div>
              <div className="text-base">
                {view.is_auto_refresh ? (
                  <span className="text-success font-semibold">Enabled</span>
                ) : (
                  <span className="text-muted-foreground font-semibold">Disabled</span>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">
                Refresh Schedule
              </div>
              <div className="text-base font-semibold">{getScheduleLabel(view.refresh_schedule)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            View Details
          </CardTitle>
          <CardDescription>Metadata and configuration information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Created At</div>
              <div className="text-base">{formatDate(view.created_at)}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Updated At</div>
              <div className="text-base">{formatDate(view.updated_at)}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Created By</div>
              <div className="text-base">
                {view.created_by_name ?? 'Unknown'}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Updated By</div>
              <div className="text-base">
                {view.updated_by ? (view.updated_by_name ?? 'Unknown') : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Rule Logic</div>
              <div className="text-base font-semibold">{view.rule_logic || 'AND'}</div>
            </div>
            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Column Selection</div>
              <div className="text-base">
                {/* Check if columns array is empty or undefined - means SELECT * */}
                {(!view.columns || view.columns.length === 0) ? (
                  <span className="text-success font-semibold">All Columns (*)</span>
                ) : (
                  <span className="text-muted-foreground font-semibold">{view.columns.length} Custom Column(s)</span>
                )}
              </div>
            </div>
          </div>

          {/* Materialization Info */}
          {(view.view_type === 'materialized' || view.is_materialized) && (
            <div className="pt-4 border-t">
              <div className="text-sm font-medium mb-3">Materialization Details</div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    Storage Location
                  </div>
                  <div className="text-sm font-mono bg-muted px-2 py-1 rounded break-all">
                    {view.materialized_location || 'Default (managed)'}
                  </div>
                </div>
                {view.materialized_at && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">
                      Materialized At
                    </div>
                    <div className="text-sm">
                      {formatDate(view.materialized_at)}
                    </div>
                  </div>
                )}
                {view.materialized_size_bytes !== undefined && view.materialized_size_bytes !== null && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">
                      Materialized Size
                    </div>
                    <div className="text-sm">
                      {formatBytes(view.materialized_size_bytes)}
                    </div>
                  </div>
                )}
                {view.partition_columns && view.partition_columns.length > 0 && (
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">
                      Partition Columns
                    </div>
                    <div className="text-sm">
                      {view.partition_columns.join(', ')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
